import { useCallback, useEffect, useState } from 'react';
import { Images } from '@urbanx/agx-ui-components';
import { WaiverOfCoolingOff, CoolingOffCondition } from '@urbanx/agx.campaigns.client';
import {
  AgxButton,
  AgxColumn,
  AgxModal,
  AgxRadioGroup,
  AgxRow,
  AgxTextInput,
  AgxTypography,
} from '@urbanx/agx.ui-components2';

const MAX_COOLING_OFF_DAYS = 5;

interface AddWaiverOfCoolingOffProps {
  id: string;
  onConfirm: (waiverOfCoolingOff: WaiverOfCoolingOff | undefined) => void;
  disabled?: boolean;
}

export function AddWaiverOfCoolingOff({
  id,
  onConfirm,
  disabled = false,
}: AddWaiverOfCoolingOffProps) {
  const [validate, setValidate] = useState(false);
  const [showCoolingOffPeriodModal, setShowCoolingOffPeriodModal] = useState(false);
  const [selectedCoolingOffCondition, setSelectedCoolingOffCondition] = useState<
    CoolingOffCondition | 'default' | undefined
  >(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [selectedNDays, setSelectedNDays] = useState<number | undefined>(undefined);
  const [nDaysError, setNDaysError] = useState<string | undefined>(undefined);

  useEffect(() => {
    setValidate(false);
  }, [selectedCoolingOffCondition, selectedNDays]);

  useEffect(() => {
    setErrorMessage(undefined);
    setNDaysError(undefined);
    setSelectedCoolingOffCondition(undefined);
    setSelectedNDays(undefined);
  }, [showCoolingOffPeriodModal]);

  const handleConfirmClick = useCallback(() => {
    setValidate(true);

    if (!selectedCoolingOffCondition) {
      setErrorMessage('Select cooling off period');
      return;
    }

    if (selectedCoolingOffCondition === CoolingOffCondition.IncludeShorteningOfCoolingOff) {
      if (selectedNDays === undefined) {
        setNDaysError('Enter number of days');
        return;
      } else if (selectedNDays <= 0) {
        setNDaysError('Number of days must be greater than 0');
        return;
      } else if (selectedNDays >= MAX_COOLING_OFF_DAYS) {
        setNDaysError(`Number of days must be less than ${MAX_COOLING_OFF_DAYS}`);
        return;
      }

      // Shortened cooling off period - store the cooling off period length
      // (in business days) after shortening.
      onConfirm({
        coolingOffCondition: CoolingOffCondition.IncludeShorteningOfCoolingOff,
        shortenCoolingOffDays: selectedNDays,
      });
      setShowCoolingOffPeriodModal(false);
      return;
    }

    // IncludeWaiverOfCoolingOff = No cooling off (buyer waived their rights)
    if (selectedCoolingOffCondition === CoolingOffCondition.IncludeWaiverOfCoolingOff) {
      onConfirm({
        coolingOffCondition: CoolingOffCondition.IncludeWaiverOfCoolingOff,
      });
      setShowCoolingOffPeriodModal(false);
      return;
    }

    // No condition = 5 business days (full cooling off - default right preserved)
    onConfirm({});
    setShowCoolingOffPeriodModal(false);
  }, [selectedCoolingOffCondition, selectedNDays, onConfirm]);

  return (
    <>
      <AgxButton
        id={`${id}-add-cooling-off-period`}
        variant="hollow"
        text="Add cooling off period"
        disabled={disabled}
        onClick={() => setShowCoolingOffPeriodModal(true)}
      />
      {showCoolingOffPeriodModal && (
        <AgxModal
          title="Cooling off period"
          type="single"
          variant="withDivider"
          actionButtonContainerStyle="withoutDivider"
          setIsDialogOpen={setShowCoolingOffPeriodModal}
          content={
            <AgxColumn
              gap="spacing-lg"
              fill
              padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
            >
              <AgxRadioGroup
                id={`${id}-cooling-off-period-type`}
                layout="vertical"
                label="Cooling off period"
                options={[
                  {
                    label: '5 business days',
                    value: 'default',
                  },
                  {
                    label: '_ business day(s)',
                    value: CoolingOffCondition.IncludeShorteningOfCoolingOff,
                  },
                  {
                    label: 'No cooling off',
                    value: CoolingOffCondition.IncludeWaiverOfCoolingOff,
                  },
                ]}
                onChange={(value) => {
                  setSelectedCoolingOffCondition(value as CoolingOffCondition | 'default');
                }}
                value={selectedCoolingOffCondition || ''}
                subContent={
                  selectedCoolingOffCondition ===
                  CoolingOffCondition.IncludeShorteningOfCoolingOff ? (
                    <>
                      <AgxRow gap="spacing-sm" padding="spacing-2xl">
                        <AgxTextInput
                          id={`${id}-cooling-off-ndays-input`}
                          placeholder="__"
                          type="number"
                          hideOptionalText
                          maxLength={1}
                          noHeader
                          noDecimalPoints
                          defaultValue={selectedNDays?.toString() || ''}
                          onChange={(value) => {
                            const selectedValue = parseInt(value, 10);
                            setSelectedNDays(!isNaN(selectedValue) ? selectedValue : undefined);
                          }}
                          required
                        />
                        <AgxColumn padding={{ top: 'spacing-md' }}>
                          <AgxTypography
                            id={`${id}-cooling-off-ndays-text`}
                            type="body_ui"
                            size="medium"
                          >
                            day(s) (less than 5)
                          </AgxTypography>
                        </AgxColumn>
                      </AgxRow>
                      {validate && nDaysError && (
                        <AgxColumn padding={{ left: 'spacing-xl', bottom: 'spacing-xl' }}>
                          <div className={'errorMessage'}>
                            <Images.AlertCircle type="Danger" />
                            <AgxTypography id={`${id}-ndays-error`} type="form_error" size="small">
                              {nDaysError}
                            </AgxTypography>
                          </div>
                        </AgxColumn>
                      )}
                    </>
                  ) : undefined
                }
              />
              {validate && errorMessage && (
                <div className={'errorMessage'}>
                  <Images.AlertCircle type="Danger" />
                  <AgxTypography id={`${id}-error`} type="form_error" size="small">
                    {errorMessage}
                  </AgxTypography>
                </div>
              )}
            </AgxColumn>
          }
          actionButtons={
            <AgxRow gap="spacing-md" alignItems="flex-end" fill padding={{ top: 'spacing-2xl' }}>
              <AgxButton
                id={`${id}-cancel-cooling-off-period`}
                variant="hollow"
                text="Cancel"
                size="large"
                stretch
                onClick={() => {
                  setShowCoolingOffPeriodModal(false);
                }}
              />
              <AgxButton
                id={`${id}-confirm-cooling-off-period`}
                variant="primary"
                text="Confirm"
                size="large"
                stretch
                onClick={handleConfirmClick}
              />
            </AgxRow>
          }
        />
      )}
    </>
  );
}
